import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createContextHardened } from '../Helper/ContextHelper';
import { scrollTop } from '../Helper/ScrollTop';
import { PageAccessMap, SubMenu } from 'src/Menus';
import useEkko from './EkkoProvider';
import useStableCallback from '../Hooks/useStableCallback';

interface Context {
  sortedMenuList: SubMenuState[];
  scrollRef: React.RefObject<HTMLDivElement>;
  scrollTop: () => void;
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  moveEntry: (subMenu: SubMenu, from: number, to: number) => void;
  toggleSubMenu: (subMenu: SubMenu) => void;
}
const [NavigationContext, useNavigation] = createContextHardened<Context>('Navigation');
export default useNavigation;

export interface SubMenuState {
  subMenu: SubMenu;
  accessKeys: string[];
  isOpen?: boolean;
}

const STORAGE_KEY_MENU: string = 'ESLMANAGER_NAVIGATION_MENU';
const defaultStatesBySubMenu: Record<SubMenu, SubMenuState> = Object.entries(PageAccessMap).reduce(
  (carry, [key, access]) => {
    const {
      menu: { subMenu },
    } = access;

    if (Object.keys(carry).includes(subMenu)) {
      carry[subMenu].accessKeys.push(key);
    } else {
      carry[subMenu] = { subMenu, accessKeys: [key], isOpen: subMenu === SubMenu.LABELING };
    }

    return carry;
  },
  {} as Record<SubMenu, SubMenuState>,
);

interface Props {
  children: React.ReactNode;
}
export const NavigationProvider = ({ children }: Props): React.JSX.Element => {
  const { hasPermission } = useEkko();
  const [isOpen, setOpen] = useState<boolean>(false);
  const [sortedMenuList, setSortedMenuList] = useState<SubMenuState[]>([]);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const scrollTopCallback = useCallback(() => {
    if (scrollRef.current === null) {
      return;
    }

    scrollTop(scrollRef.current);
  }, []);

  const moveEntry = useCallback(
    (subMenu: SubMenu, from: number, to: number) => {
      const subMenuIndex = sortedMenuList.findIndex((m) => m.subMenu === subMenu);
      if (subMenuIndex >= 0 && from >= 0 && to >= 0 && from !== to) {
        // working on a copy
        const newOrderedMenu = [...sortedMenuList];
        const subMenu = newOrderedMenu[subMenuIndex];

        if (subMenu.accessKeys.length - 1 > to) {
          const [moved] = subMenu.accessKeys.splice(from, 1);
          subMenu.accessKeys.splice(to, 0, moved);
          newOrderedMenu[subMenuIndex] = subMenu;

          setSortedMenuList(newOrderedMenu);
        }
      }
    },
    [sortedMenuList],
  );

  const toggleSubMenu = useCallback(
    (subMenu: SubMenu) => {
      const subMenuIndex = sortedMenuList.findIndex((m) => m.subMenu === subMenu);

      if (subMenuIndex >= 0) {
        const menu = sortedMenuList[subMenuIndex];
        sortedMenuList[subMenuIndex].isOpen = !menu.isOpen;
        setSortedMenuList([...sortedMenuList]);
      }
    },
    [sortedMenuList],
  );

  useEffect(() => {
    const storedMenuList = localStorage.getItem(STORAGE_KEY_MENU);
    const decodedList = storedMenuList === null ? [] : JSON.parse(storedMenuList);

    if (!Array.isArray(decodedList) || decodedList.length === 0) {
      // if we can't restore anything, we fallback to the default.
      setSortedMenuList(Object.values(defaultStatesBySubMenu));
      return;
    }

    const subMenuOrder: SubMenu[] = [];
    const decodedStatesBySubMenu: Record<SubMenu, SubMenuState> = decodedList.reduce(
      (carry, m) => {
        // check for old data we may have to repair
        if (m.title !== undefined) {
          const defaultState = defaultStatesBySubMenu[m.title as SubMenu];

          // do not include unknown submenu
          if (defaultState === undefined) {
            return carry;
          }
          carry[m.title as SubMenu] = defaultState;
          subMenuOrder.push(m.title as SubMenu);
        } else {
          const state = m as SubMenuState;
          const defaultState = defaultStatesBySubMenu[state.subMenu];

          // do not include unknown submenu
          if (defaultState === undefined) {
            return carry;
          }

          // only keep known access keys
          state.accessKeys = state.accessKeys.filter((k) => defaultState.accessKeys.includes(k));

          // append missing known access keys
          const missingEntries = defaultState.accessKeys.filter((k) => !state.accessKeys.includes(k));
          missingEntries.forEach((k) => {
            state.accessKeys.push(k);
          });

          carry[state.subMenu] = state;
          subMenuOrder.push(state.subMenu);
        }

        return carry;
      },
      {} as Record<SubMenu, SubMenuState>,
    );

    // add entirely new / missing sub menus
    const defaultSubMenuKeys = Object.keys(defaultStatesBySubMenu);
    const missingSubMenuKeys = defaultSubMenuKeys.filter((k) => !subMenuOrder.includes(k as SubMenu));
    missingSubMenuKeys.forEach((k) => {
      const sm = k as SubMenu;
      subMenuOrder.push(sm);
      decodedStatesBySubMenu[sm] = defaultStatesBySubMenu[sm];
    });

    setSortedMenuList(Object.values(decodedStatesBySubMenu));
  }, []);

  /** store on sorting change */
  useEffect(() => {
    if (sortedMenuList.length === 0) {
      return;
    }
    localStorage.setItem(STORAGE_KEY_MENU, JSON.stringify(sortedMenuList));
  }, [sortedMenuList]);

  const storageEventHandler = useStableCallback((event: StorageEvent) => {
    switch (event.key) {
      case STORAGE_KEY_MENU:
        if (event.newValue !== null && !['', '[]'].includes(event.newValue)) {
          setSortedMenuList(JSON.parse(event.newValue));
        }
        break;
    }
  }, []);

  /** sync storage accross contexts */
  useEffect(() => {
    addEventListener('storage', storageEventHandler);
    return () => {
      removeEventListener('storage', storageEventHandler);
    };
  }, [storageEventHandler]);

  const permittedMenuList = useMemo(
    () =>
      sortedMenuList.map((sm) => {
        const { accessKeys } = sm;
        const permittedKeys = accessKeys.filter((k) => hasPermission(PageAccessMap[k].permission));
        return { ...sm, accessKeys: permittedKeys };
      }),
    [sortedMenuList, hasPermission],
  );

  return (
    <NavigationContext.Provider
      value={{
        scrollRef,
        sortedMenuList: permittedMenuList,
        scrollTop: scrollTopCallback,
        isOpen,
        setOpen,
        moveEntry,
        toggleSubMenu,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};
